import { useState, useEffect, useCallback } from 'react';
import {
  AlertOctagon,
  Brain,
  Bug,
  Sparkles,
  MessageSquare,
  Loader2,
  CheckCircle2,
  XCircle,
  CircleDot,
  PlayCircle,
  RefreshCw,
  Image as ImageIcon,
  Monitor,
  User,
  Activity,
  ArrowUpRight,
} from 'lucide-react';
import { feedbackApi, workshopApi } from '../../services/api';
import {
  type Feedback,
  type FeedbackCategory,
  type FeedbackStatus,
  FEEDBACK_CATEGORY_LABEL,
  FEEDBACK_STATUS_LABEL,
  FEEDBACK_SUBCATEGORY_LABEL,
} from '../../types';

const CATEGORY_META: Record<FeedbackCategory, { icon: typeof Bug; color: string }> = {
  critical: { icon: AlertOctagon, color: 'text-red-600 dark:text-red-400' },
  model_quality: { icon: Brain, color: 'text-purple-600 dark:text-purple-400' },
  ui_bug: { icon: Bug, color: 'text-amber-600 dark:text-amber-400' },
  ease_of_life: { icon: Sparkles, color: 'text-blue-600 dark:text-blue-400' },
  other: { icon: MessageSquare, color: 'text-gray-600 dark:text-gray-400' },
};

const STATUS_META: Record<FeedbackStatus, { icon: typeof CircleDot; color: string }> = {
  new: { icon: CircleDot, color: 'text-blue-600 dark:text-blue-400' },
  in_progress: { icon: PlayCircle, color: 'text-amber-600 dark:text-amber-400' },
  resolved: { icon: CheckCircle2, color: 'text-green-600 dark:text-green-400' },
  wont_fix: { icon: XCircle, color: 'text-gray-500 dark:text-gray-500' },
};

function formatDate(s: string): string {
  return new Date(s).toLocaleString();
}

// Human-readable signal labels — keep in sync with backend constants in
// app/services/error_detection_service.py.
const SIGNAL_LABELS: Record<string, string> = {
  contrast_vocab_in_noncontrast: 'Contrast vocab on non-contrast study',
};

export default function FeedbackInbox() {
  const [items, setItems] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<FeedbackStatus | ''>('');
  const [categoryFilter, setCategoryFilter] = useState<FeedbackCategory | ''>('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [screenshotUrl, setScreenshotUrl] = useState<string | null>(null);
  const [screenshotLoading, setScreenshotLoading] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState('');
  const [signalCounts, setSignalCounts] = useState<{ counts: Record<string, number>; total: number; days: number } | null>(null);
  const [promoting, setPromoting] = useState(false);
  const [promoteResult, setPromoteResult] = useState<string | null>(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [list, signals] = await Promise.all([
        feedbackApi.list({
          status: statusFilter || undefined,
          category: categoryFilter || undefined,
          limit: 200,
        }),
        feedbackApi.signalCounts(30).catch(() => null),
      ]);
      setItems(list);
      setSignalCounts(signals);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load feedback');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, categoryFilter]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const selected = items.find((i) => i.id === selectedId) ?? null;

  // Load screenshot when a feedback row is selected
  useEffect(() => {
    if (screenshotUrl) {
      URL.revokeObjectURL(screenshotUrl);
      setScreenshotUrl(null);
    }
    if (!selected || !selected.has_screenshot) return;
    setScreenshotLoading(true);
    let cancelled = false;
    feedbackApi
      .fetchScreenshot(selected.id)
      .then((url) => {
        if (!cancelled) setScreenshotUrl(url);
      })
      .catch(() => {
        if (!cancelled) setScreenshotUrl(null);
      })
      .finally(() => {
        if (!cancelled) setScreenshotLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  // Sync notes draft when selection changes
  useEffect(() => {
    setNotesDraft(selected?.admin_notes ?? '');
    setPromoteResult(null);
  }, [selectedId, selected?.admin_notes]);

  const handlePromote = async () => {
    if (!selected) return;
    setPromoting(true);
    setPromoteResult(null);
    try {
      const priority = selected.category === 'critical' ? 'critical' as const : 'medium' as const;
      const item = await workshopApi.promoteFromFeedback({
        feedback_id: selected.id,
        priority,
      });
      setPromoteResult(`Promoted → backlog item ${item.id.slice(0, 8)}`);
    } catch (e) {
      setPromoteResult(e instanceof Error ? e.message : 'Failed to promote');
    } finally {
      setPromoting(false);
    }
  };

  const handleStatusChange = async (status: FeedbackStatus) => {
    if (!selected) return;
    const updated = await feedbackApi.update(selected.id, { status });
    setItems((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
  };

  const handleSaveNotes = async () => {
    if (!selected) return;
    setSavingNotes(true);
    try {
      const updated = await feedbackApi.update(selected.id, { admin_notes: notesDraft });
      setItems((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
    } finally {
      setSavingNotes(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Filters */}
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <div>
          <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">Status</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as FeedbackStatus | '')}
            className="px-2 py-1 text-sm border border-gray-300 dark:border-surface-800 rounded bg-white dark:bg-surface-850 text-gray-900 dark:text-white"
          >
            <option value="">All</option>
            <option value="new">New</option>
            <option value="in_progress">In progress</option>
            <option value="resolved">Resolved</option>
            <option value="wont_fix">Won't fix</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">Category</label>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value as FeedbackCategory | '')}
            className="px-2 py-1 text-sm border border-gray-300 dark:border-surface-800 rounded bg-white dark:bg-surface-850 text-gray-900 dark:text-white"
          >
            <option value="">All</option>
            <option value="critical">Critical</option>
            <option value="model_quality">Report quality</option>
            <option value="ui_bug">UI bug</option>
            <option value="ease_of_life">Ease of life</option>
            <option value="other">Other</option>
          </select>
        </div>
        <button
          onClick={fetchList}
          className="ml-auto mt-4 inline-flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 dark:bg-surface-850 hover:bg-gray-200 dark:hover:bg-surface-800 rounded text-gray-700 dark:text-gray-300"
        >
          <RefreshCw size={12} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="p-2 mb-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 rounded text-xs text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {/* Passive signal counts — data collection tile */}
      {signalCounts && (
        <div className="mb-3 p-3 border border-gray-200 dark:border-surface-800 rounded bg-white dark:bg-surface-900">
          <div className="flex items-center gap-2 mb-2">
            <Activity size={14} className="text-primary-600 dark:text-primary-400" />
            <span className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
              Passive error signals
            </span>
            <span className="text-xs text-gray-400">
              last {signalCounts.days} days — {signalCounts.total} total
            </span>
          </div>
          {Object.keys(signalCounts.counts).length === 0 ? (
            <p className="text-xs text-gray-500 dark:text-gray-400">No signals detected yet. Generate some reports to start gathering data.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {Object.entries(signalCounts.counts).map(([type, cnt]) => (
                <div
                  key={type}
                  className="px-2 py-1 rounded bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/40 text-xs text-amber-800 dark:text-amber-300"
                  title={type}
                >
                  <span className="font-medium">{SIGNAL_LABELS[type] || type}</span>
                  <span className="ml-1.5 font-mono opacity-75">×{cnt}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Master / detail split */}
      <div className="grid gap-3 flex-1 min-h-0" style={{ gridTemplateColumns: '24rem 1fr' }}>
        {/* List */}
        <div className="border border-gray-200 dark:border-surface-800 rounded overflow-y-auto bg-white dark:bg-surface-900">
          {loading ? (
            <div className="p-4 text-center text-sm text-gray-500"><Loader2 size={16} className="animate-spin inline mr-2" />Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-4 text-center text-sm text-gray-500">No feedback matches the current filters.</div>
          ) : (
            items.map((item) => {
              const CatIcon = CATEGORY_META[item.category].icon;
              const StatusIcon = STATUS_META[item.status].icon;
              const isSelected = item.id === selectedId;
              return (
                <button
                  key={item.id}
                  onClick={() => setSelectedId(item.id)}
                  className={`block w-full text-left px-3 py-2 border-b border-gray-100 dark:border-surface-800 ${
                    isSelected ? 'bg-primary-50 dark:bg-primary-900/20' : 'hover:bg-gray-50 dark:hover:bg-surface-850'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <CatIcon size={14} className={CATEGORY_META[item.category].color} />
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                      {FEEDBACK_CATEGORY_LABEL[item.category]}
                    </span>
                    {item.subcategory && (
                      <span className="text-[10px] text-gray-500 dark:text-gray-400 px-1.5 py-0.5 rounded bg-gray-100 dark:bg-surface-850">
                        {FEEDBACK_SUBCATEGORY_LABEL[item.subcategory]}
                      </span>
                    )}
                    <StatusIcon size={12} className={`ml-auto ${STATUS_META[item.status].color}`} />
                  </div>
                  <p className="text-xs text-gray-900 dark:text-white line-clamp-2">{item.content}</p>
                  <div className="mt-1 text-[10px] text-gray-500 dark:text-gray-400 flex items-center gap-2">
                    <span>{item.user_email ?? 'unknown user'}</span>
                    <span>•</span>
                    <span>{formatDate(item.created_at)}</span>
                    {item.has_screenshot && <ImageIcon size={10} className="ml-auto" />}
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* Detail */}
        <div className="border border-gray-200 dark:border-surface-800 rounded overflow-y-auto bg-white dark:bg-surface-900 p-4">
          {!selected ? (
            <div className="text-sm text-gray-500 text-center mt-12">Select a feedback item to see details.</div>
          ) : (
            <div className="space-y-4">
              {/* Header */}
              <div>
                <div className="flex items-center gap-2 mb-1">
                  {(() => { const I = CATEGORY_META[selected.category].icon; return <I size={18} className={CATEGORY_META[selected.category].color} />; })()}
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">{FEEDBACK_CATEGORY_LABEL[selected.category]}</span>
                  {selected.subcategory && (
                    <span className="text-xs px-2 py-0.5 rounded bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 font-medium">
                      {FEEDBACK_SUBCATEGORY_LABEL[selected.subcategory]}
                    </span>
                  )}
                  <span className="ml-auto text-xs text-gray-500">{formatDate(selected.created_at)}</span>
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                  <User size={12} />
                  {selected.user_email ?? 'unknown user'}
                </div>
              </div>

              {/* Status controls + promote */}
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-gray-500 dark:text-gray-400">Status:</span>
                {(['new', 'in_progress', 'resolved', 'wont_fix'] as FeedbackStatus[]).map((s) => (
                  <button
                    key={s}
                    onClick={() => handleStatusChange(s)}
                    className={`px-2 py-1 text-xs rounded font-medium ${
                      selected.status === s
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 dark:bg-surface-850 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-surface-800'
                    }`}
                  >
                    {FEEDBACK_STATUS_LABEL[s]}
                  </button>
                ))}
                <button
                  onClick={handlePromote}
                  disabled={promoting}
                  title="Create a backlog item from this feedback (idempotent — clicking again returns the existing item)"
                  className="ml-auto inline-flex items-center gap-1 px-2 py-1 text-xs bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white rounded font-medium"
                >
                  <ArrowUpRight size={12} />
                  {promoting ? 'Promoting…' : 'Promote to backlog'}
                </button>
              </div>
              {promoteResult && (
                <div className="text-xs text-gray-600 dark:text-gray-400 italic">{promoteResult}</div>
              )}

              {/* Content */}
              <div>
                <h3 className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400 mb-1">Feedback</h3>
                <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">{selected.content}</p>
              </div>

              {/* Screenshot */}
              {selected.has_screenshot && (
                <div>
                  <h3 className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400 mb-1">Screenshot</h3>
                  {screenshotLoading ? (
                    <div className="text-xs text-gray-500"><Loader2 size={12} className="animate-spin inline mr-1" />Loading…</div>
                  ) : screenshotUrl ? (
                    <a href={screenshotUrl} target="_blank" rel="noreferrer">
                      <img src={screenshotUrl} alt="User screenshot" className="max-w-full border border-gray-200 dark:border-surface-800 rounded" />
                    </a>
                  ) : (
                    <div className="text-xs text-gray-500">Screenshot unavailable.</div>
                  )}
                </div>
              )}

              {/* Session context */}
              <div>
                <h3 className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400 mb-1">Session context</h3>
                <dl className="grid grid-cols-[8rem_1fr] gap-x-2 gap-y-1 text-xs">
                  {selected.model_used && (<><dt className="text-gray-500">Model used</dt><dd className="text-gray-900 dark:text-white font-mono">{selected.model_used}</dd></>)}
                  {selected.primary_model && selected.primary_model !== selected.model_used && (<><dt className="text-gray-500">Primary model</dt><dd className="text-gray-900 dark:text-white font-mono">{selected.primary_model} (chain fell through)</dd></>)}
                  {selected.exam_category && (<><dt className="text-gray-500">Exam category</dt><dd className="text-gray-900 dark:text-white">{selected.exam_category}</dd></>)}
                  {selected.template_name && (<><dt className="text-gray-500">Template</dt><dd className="text-gray-900 dark:text-white">{selected.template_name}</dd></>)}
                  {selected.url && (<><dt className="text-gray-500">URL</dt><dd className="text-gray-900 dark:text-white truncate">{selected.url}</dd></>)}
                  {selected.viewport_width && (<><dt className="text-gray-500">Viewport</dt><dd className="text-gray-900 dark:text-white"><Monitor size={10} className="inline mr-1" />{selected.viewport_width} × {selected.viewport_height}</dd></>)}
                </dl>
              </div>

              {selected.transcription && (
                <details>
                  <summary className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400 cursor-pointer">Transcription</summary>
                  <pre className="text-xs text-gray-900 dark:text-white whitespace-pre-wrap mt-1 p-2 bg-gray-50 dark:bg-surface-850 rounded">{selected.transcription}</pre>
                </details>
              )}

              {selected.raw_report && (
                <details>
                  <summary className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400 cursor-pointer">Generated report (raw)</summary>
                  <div className="text-xs text-gray-900 dark:text-white mt-1 p-2 bg-gray-50 dark:bg-surface-850 rounded prose prose-sm dark:prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: selected.raw_report }} />
                </details>
              )}

              {selected.final_report && selected.final_report !== selected.raw_report && (
                <details>
                  <summary className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400 cursor-pointer">Final report (after edits)</summary>
                  <div className="text-xs text-gray-900 dark:text-white mt-1 p-2 bg-gray-50 dark:bg-surface-850 rounded prose prose-sm dark:prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: selected.final_report }} />
                </details>
              )}

              {/* Admin notes */}
              <div>
                <h3 className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400 mb-1">Admin notes</h3>
                <textarea
                  value={notesDraft}
                  onChange={(e) => setNotesDraft(e.target.value)}
                  rows={3}
                  placeholder="Investigation notes, repro steps, resolution…"
                  className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-surface-800 rounded bg-white dark:bg-surface-850 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
                <div className="mt-1 flex justify-end">
                  <button
                    onClick={handleSaveNotes}
                    disabled={savingNotes || notesDraft === (selected.admin_notes ?? '')}
                    className="px-2 py-1 text-xs bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded font-medium"
                  >
                    {savingNotes ? 'Saving…' : 'Save notes'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
